import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Helmet } from "react-helmet-async";
import { Shield, Cookie, Lock } from "lucide-react";
import { useEffect } from "react";

export function Legal() {
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        const headerOffset = 120;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
    }
  }, []);
  return (
    <>
      <Helmet>
        <title>Políticas de Privacidade, Cookies e Segurança - Portal Ethics</title>
        <meta name="description" content="Conhepolíticas de privacidade, uso de cookies e medidas de segurança do Portal Ethics." />
      </Helmet>

      <div className="min-h-screen bg-background-light">
        <Header />
        
        <main className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-6">
            {/* Header */}
            <div className="text-center mb-16">
              <h1 className="text-neutral-900 text-4xl font-bold mb-4">
                Políticas de Privacidade, Cookies e Segurança
              </h1>
              <p className="text-neutral-600 text-lg">
                Transparência e proteção dos seus dados são nossas prioridades
              </p>
            </div>

            {/* Privacidade */}
            <div id="privacidade" className="bg-white rounded-xl p-8 mb-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-primary-blue/10 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-primary-blue" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900">Privacidade</h2>
              </div>
              
              <div className="space-y-4 text-neutral-600">
                <p>
                  O Portal Ethics está comprometido com a proteção da sua privacidade e dos seus dados pessoais, 
                  em conformidade com a Lei Geral de Proteção de Dados (LGPD) e outras regulamentações aplicáveis.
                </p>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Coleta de Dados</h3>
                  <p>
                    Coletamos informações que você nos fornece diretamente, como nome, email, telefone e dados da empresa, 
                    bem como informações geradas automaticamente durante o uso da plataforma, como logs de acesso e 
                    dados de navegação.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Uso das Informações</h3>
                  <p>
                    Utilizamos seus dados para: fornecer nossos serviços, processar pagamentos, enviar comunicações 
                    sobre sua conta, melhorar nossa plataforma, cumprir obrigações legais e proteger contra atividades 
                    fraudulentas.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Compartilhamento de Dados</h3>
                  <p>
                    Não vendemos suas informações pessoais. Podemos compartilhar dados com parceiros de serviço 
                    estritamente necessários para operar nossa plataforma, como processadores de pagamento e serviços 
                    de hospedagem, sempre sob contratos que garantem a segurança dos dados.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Seus Direitos</h3>
                  <p>
                    Você tem direito de: acessar seus dados, corrigir informações incompletas, solicitar exclusão de 
                    dados pessoais, revogar consentimentos, opor-se ao processamento e solicitar portabilidade de dados. 
                    Para exercer esses direitos, entre em contato através do e-mail contato@etcs.com.br.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Armazenamento e Segurança</h3>
                  <p>
                    Seus dados são armazenados em servidores seguros com criptografia de ponta a ponta. Mantemos 
                    medidas técnicas e organizacionais adequadas para proteger suas informações contra acesso não 
                    autorizado, perda ou destruição.
                  </p>
                </div>
              </div>
            </div>

            {/* Cookies */}
            <div id="cookies" className="bg-white rounded-xl p-8 mb-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-primary-orange/10 rounded-lg flex items-center justify-center">
                  <Cookie className="w-6 h-6 text-primary-orange" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900">Cookies</h2>
              </div>
              
              <div className="space-y-4 text-neutral-600">
                <p>
                  Utilizamos cookies e tecnologias similares para melhorar sua experiência, personalizar conteúdo e 
                  analisar o uso da plataforma. Ao continuar navegando, você concorda com o uso de cookies conforme 
                  descrito nesta política.
                </p>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">O que são Cookies?</h3>
                  <p>
                    Cookies são pequenos arquivos de texto armazenados no seu dispositivo que contêm informações sobre 
                    sua navegação. Eles nos ajudam a lembrar suas preferências e melhorar a funcionalidade do site.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Tipos de Cookies Utilizados</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      <strong className="text-neutral-900">Cookies Essenciais:</strong> Necessários para o funcionamento 
                      básico da plataforma, como autenticação e segurança.
                    </li>
                    <li>
                      <strong className="text-neutral-900">Cookies de Desempenho:</strong> Coletam informações sobre como 
                      você usa o site para melhorar a experiência do usuário.
                    </li>
                    <li>
                      <strong className="text-neutral-900">Cookies de Funcionalidade:</strong> Permitem que o site lembbre 
                      suas escolhas e preferências.
                    </li>
                    <li>
                      <strong className="text-neutral-900">Cookies de Marketing:</strong> Usados para personalizar 
                      conteúdo e anúncios relevantes.
                    </li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Gerenciamento de Cookies</h3>
                  <p>
                    Você pode configurar seu navegador para recusar cookies ou excluir cookies já armazenados. No 
                    entanto, isso pode afetar a funcionalidade da plataforma e limitar sua experiência de uso.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Cookies de Terceiros</h3>
                  <p>
                    Utilizamos serviços de terceiros, como Google Analytics, que podem coletar informações através 
                      de cookies. Esses serviços têm suas próprias políticas de privacidade e uso de cookies.
                  </p>
                </div>
              </div>
            </div>

            {/* Segurança */}
            <div id="seguranca" className="bg-white rounded-xl p-8 mb-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <Lock className="w-6 h-6 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900">Segurança</h2>
              </div>
              
              <div className="space-y-4 text-neutral-600">
                <p>
                  A segurança dos seus dados e da nossa plataforma é nossa prioridade absoluta. Implementamos múltiplas 
                  camadas de proteção e seguimos as melhores práticas da indústria para garantir a integridade e 
                  confidencialidade das informações.
                </p>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Criptografia de Dados</h3>
                  <p>
                    Todos os dados sensíveis são criptografados usando protocolos SSL/TLS durante a transmissão. 
                    Dados armazenados são protegidos com criptografia de nível bancário e controlamos rigorosamente 
                    o acesso às chaves de criptografia.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Proteção Contra Ataques</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      <strong className="text-neutral-900">Firewall e WAF:</strong> Sistemas avançados de proteção 
                      contra ataques de injeção, DDoS e outras ameaças cibernéticas.
                    </li>
                    <li>
                      <strong className="text-neutral-900">Autenticação Forte:</strong> Senhas com hash seguro, 
                      autenticação de dois fatores e sessões com tempo limitado.
                    </li>
                    <li>
                      <strong className="text-neutral-900">Validação de Entrada:</strong> Verificação rigorosa 
                      de todos os dados recebidos para prevenir ataques.
                    </li>
                    <li>
                      <strong className="text-neutral-900">Proteção de API:</strong> Rate limiting, autenticação 
                      e autorização em todos os endpoints.
                    </li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Monitoramento de Segurança</h3>
                  <p>
                    Monitoramos continuamente nossa plataforma para detectar atividades suspeitas. Realizamos auditorias 
                    de segurança regulares, testes de penetração e mantemos nossos sistemas atualizados com as 
                    últimas correções de segurança.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Backup e Recuperação</h3>
                  <p>
                    Realizamos backups automáticos e redundantes de todos os dados. Possuímos planos de recuperação 
                    de desastres testados regularmente para garantir continuidade de negócios e proteção contra 
                    perda de dados.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Conformidade e Certificações</h3>
                  <p>
                    Seguimos padrões internacionais de segurança, como ISO 27001, e estamos em conformidade com 
                    regulamentações como LGPD, PCI DSS e outros requisitos aplicáveis ao nosso segmento.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">Responsabilidade do Usuário</h3>
                  <p>
                    Para manter a segurança da sua conta, recomendamos: usar senhas fortes e únicas, não compartilhar 
                    credenciais, manter dispositivos atualizados, e notificar imediatamente qualquer atividade 
                    suspeita através do e-mail contato@etcs.com.br.
                  </p>
                </div>
              </div>
            </div>

            {/* Última Atualização */}
            <div className="text-center text-sm text-neutral-500 mt-12">
              <p>Última atualização: Julho de 2026</p>
              <p className="mt-2">
                Para dúvidas ou mais informações, entre em contato:{" "}
                <a href="mailto:contato@etcs.com.br" className="text-primary-blue hover:underline">
                  contato@etcs.com.br
                </a>
              </p>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}